import React from 'react';
import { cn } from '../../lib/utils';
import { Car, Train, Bike } from 'lucide-react';

const transitOptions = [
  { value: 'car', label: 'Car', icon: Car },
  { value: 'public_transport', label: 'Public transport', icon: Train },
  { value: 'bike', label: 'Bike', icon: Bike },
];

export function TransitModeSelect({ value, onChange, className }) {
  // value is now an array of selected transit modes
  const selected = Array.isArray(value) ? value : value ? [value] : [];

  const toggle = (mode) => {
    if (selected.includes(mode)) {
      onChange(selected.filter(v => v !== mode));
    } else {
      onChange([...selected, mode]);
    }
  };

  return (
    <div className={cn('grid grid-cols-2 sm:grid-cols-3 gap-3', className)}>
      {transitOptions.map((option) => {
        const Icon = option.icon;
        const isSelected = selected.includes(option.value);
        
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => toggle(option.value)}
            className={cn(
              'flex flex-col items-center justify-center gap-2 p-4 rounded-xl border transition-all min-h-[100px]',
              isSelected
                ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                : 'bg-[#151515] border-white/[0.08] text-white/60 hover:bg-white/[0.05] hover:border-white/20'
            )}
            data-testid={`transit-${option.value}`}
          >
            <Icon className={cn('h-6 w-6', isSelected ? 'text-amber-400' : 'text-white/50')} />
            <span className="text-sm font-medium">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

const radiusPresets = [10, 25, 50, 75, 100, 150];

export function RadiusSlider({ value, onChange, className }) {
  return (
    <div className={cn('space-y-4', className)}>
      {/* Preset buttons */}
      <div className="flex flex-wrap gap-2 justify-center">
        {radiusPresets.map((preset) => {
          const isSelected = value === preset;
          return (
            <button
              key={preset}
              type="button"
              onClick={() => onChange(preset)}
              className={cn(
                'px-4 py-2.5 rounded-lg text-sm font-medium transition-all min-w-[70px]',
                isSelected
                  ? 'bg-amber-500 text-black'
                  : 'bg-[#1a1a1a] border border-white/[0.08] text-white/70 hover:bg-white/[0.08]'
              )}
              data-testid={`radius-${preset}`}
            >
              {preset} km
            </button>
          );
        })}
      </div>
      
      {/* Slider */}
      <div className="relative pt-2">
        <input
          type="range"
          min="10"
          max="200"
          step="5"
          value={value || 50}
          onChange={(e) => onChange(parseInt(e.target.value))}
          className="w-full h-2 bg-[#1a1a1a] rounded-full appearance-none cursor-pointer
            [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 
            [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-500 
            [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg
            [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full 
            [&::-moz-range-thumb]:bg-amber-500 [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
          data-testid="radius-slider"
        />
        <div className="flex justify-between text-xs text-white/30 mt-1">
          <span>10 km</span>
          <span>200 km</span>
        </div>
      </div>

      {/* Current value display */}
      <div className="text-center">
        <span className="text-3xl font-semibold text-white">{value || 50}</span>
        <span className="text-white/50 ml-2">km</span>
      </div>
    </div>
  );
}
